import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { exchangeCodeForToken } from '../api/auth'
import { setItem, getItem, removeItem } from '../utils/local-storage'
import { getOAuthUrl } from '../utils/oauth'
import { isTokenValid } from '../utils/validate-token'
import { clearCatalogSyncSession } from '../utils/catalogSyncSession'
import { clearSessionRequestCache } from '../utils/sessionRequestCache'

interface IAuthContext {
  token: string | null
  isAuthenticated: boolean
  authenticate: () => void
  logout: () => void
  handleOAuthCallback: (code: string) => void
}

const AuthContext = createContext<IAuthContext | null>(null)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [token, setToken] = useState<string | null>(getItem('token') || null)
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
  const navigate = useNavigate()

  const logout = useCallback(() => {
    removeItem('token')
    clearCatalogSyncSession()
    clearSessionRequestCache()
    setToken(null)
    setIsAuthenticated(false)
    navigate('/')
  }, [navigate])

  useEffect(() => {
    const validateToken = async () => {
      if (token) {
        const valid = await isTokenValid(token)
        if (!valid) {
          logout()
        } else {
          setIsAuthenticated(true)
        }
      } else {
        setIsAuthenticated(false)
      }
    }

    void validateToken()
  }, [token, logout])

  const authenticate = () => {
    const oauthUrl = getOAuthUrl()
    window.location.href = oauthUrl
  }

  const handleOAuthCallback = async (code: string) => {
    await exchangeCodeForToken(code)
      .then((data) => {
        const accessToken = data.access_token
        setItem('token', accessToken)
        setToken(accessToken)
        setIsAuthenticated(true)
        navigate('/')
      })
      .catch((error) => {
        console.error('Error obtaining token', error)
      })
  }

  return (
    <AuthContext.Provider
      value={{
        token,
        isAuthenticated,
        authenticate,
        handleOAuthCallback,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider')
  }
  return context
}
